import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

export const useSupportArticleStyles = makeStyles({ name: 'SupportArticle' })(theme => ({
  content: {
    maxWidth: 900,
    margin: '0 auto',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(6),
  },
  section: {
    marginTop: theme.spacing(4),
  },
  subsection: {
    marginTop: theme.spacing(3),
  },
  paragraph: {
    marginTop: theme.spacing(1.5),
  },
  example: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    overflowX: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  table: {
    minWidth: 560,
  },
  tableHeaderCell: {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableCell: {
    verticalAlign: 'top',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableCodeCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  list: {
    listStyleType: 'disc',
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
  },
  listItem: {
    display: 'list-item',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: 0,
  },
  nestedList: {
    listStyleType: 'circle',
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(0.5),
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 3),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

type ArticleSectionProps = {
  title: string;
  children: React.ReactNode;
};

export const ArticleSection: React.FC<ArticleSectionProps> = ({ title, children }) => {
  const { classes } = useSupportArticleStyles();

  return (
    <Box className={classes.section}>
      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

type ArticleSubsectionProps = {
  title: string;
  children: React.ReactNode;
};

export const ArticleSubsection: React.FC<ArticleSubsectionProps> = ({ title, children }) => {
  const { classes } = useSupportArticleStyles();

  return (
    <Box className={classes.subsection}>
      <Typography variant="h6" component="h3" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

export const ArticleParagraph: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { classes } = useSupportArticleStyles();

  return (
    <Typography variant="body1" className={classes.paragraph}>
      {children}
    </Typography>
  );
};

export const ArticleBulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => {
  const { classes } = useSupportArticleStyles();

  return (
    <List className={classes.list} disablePadding>
      {items.map((item, index) => (
        <ListItem key={index} className={classes.listItem} disableGutters>
          <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1' }} />
        </ListItem>
      ))}
    </List>
  );
};

export const ArticleExample: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => {
  const { classes } = useSupportArticleStyles();

  return (
    <Paper elevation={0} className={classes.example}>
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Paper>
  );
};

type SimpleTableProps = {
  headers: string[];
  rows: React.ReactNode[][];
  /** Treat the first column as a code/key column (bold, nowrap). Defaults to true. */
  codeFirstColumn?: boolean;
  columnWidths?: string[];
};

export const ArticleTable: React.FC<SimpleTableProps> = ({ headers, rows, codeFirstColumn = true, columnWidths }) => {
  const { classes } = useSupportArticleStyles();

  return (
    <TableContainer className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={header} className={classes.tableHeaderCell} sx={{ width: columnWidths?.[index] }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell
                  key={cellIndex}
                  className={codeFirstColumn && cellIndex === 0 ? classes.tableCodeCell : classes.tableCell}
                >
                  <Typography variant="body2" component="div">
                    {cell}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
